/**
 * Recherche et tri côté client. Volontairement simple : les volumes d'une section
 * (quelques dizaines de personnes, quelques centaines de jeux) tiennent en mémoire,
 * ce qui permet une recherche instantanée y compris hors connexion.
 */

/** Minuscule sans accents ni ponctuation, pour comparer « Noël » et « noel ». */
export function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\u2019']/g, ' ')
    .trim();
}

export function tokenize(query: string): string[] {
  return normalize(query).split(/[\s,;]+/).filter(Boolean);
}

/**
 * `true` si chaque terme de la requête apparaît dans au moins un des champs.
 * Les termes sont conjonctifs (ET) : « jeu ballon » ne remonte que les jeux
 * contenant les deux mots, ce qui affine la recherche au fil de la frappe.
 */
export function matchesQuery(query: string, fields: Array<string | null | undefined>): boolean {
  const terms = tokenize(query);
  if (terms.length === 0) return true;
  const haystack = fields.filter(Boolean).map((f) => normalize(String(f))).join(' | ');
  return terms.every((term) => haystack.includes(term));
}

/**
 * Score de pertinence pour classer les résultats : préfixe exact > début de mot >
 * sous-chaîne. Les champs en tête de `fields` pèsent davantage.
 */
export function relevanceScore(query: string, fields: Array<string | null | undefined>): number {
  const terms = tokenize(query);
  if (terms.length === 0) return 0;
  let score = 0;
  fields.forEach((field, fieldIndex) => {
    if (!field) return;
    const value = normalize(String(field));
    const weight = Math.max(1, fields.length - fieldIndex);
    for (const term of terms) {
      const at = value.indexOf(term);
      if (at < 0) continue;
      if (value === term) score += 100 * weight;
      else if (at === 0) score += 40 * weight;
      else if (/\s/.test(value.charAt(at - 1))) score += 20 * weight;
      else score += 5 * weight;
    }
  });
  return score;
}

export type SortDirection = 'asc' | 'desc';

/**
 * Comparateur stable et sensible à la locale française (tri correct des accents).
 * Les valeurs nulles sont toujours reléguées en fin de liste.
 */
export function comparator<T>(
  selector: (item: T) => string | number | null | undefined,
  direction: SortDirection = 'asc',
): (a: T, b: T) => number {
  const collator = new Intl.Collator('fr-BE', { numeric: true, sensitivity: 'base' });
  const sign = direction === 'asc' ? 1 : -1;
  return (a, b) => {
    const va = selector(a);
    const vb = selector(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    return collator.compare(String(va), String(vb)) * sign;
  };
}

/** Nom affichable : totem en priorité, sinon prénom + nom. */
export function displayName(person: {
  firstName: string;
  lastName: string;
  totem?: string | null;
}): string {
  if (person.totem) return person.totem;
  return `${person.firstName} ${person.lastName}`.trim();
}

export function fullName(person: { firstName: string; lastName: string }): string {
  return `${person.firstName} ${person.lastName}`.trim();
}

/** Initiales pour les avatars de repli (2 caractères max). */
export function initials(person: { firstName: string; lastName: string }): string {
  const a = person.firstName.trim().charAt(0);
  const b = person.lastName.trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

/** Découpe une saisie libre en liste (matériel, tags…). */
export function parseList(input: string): string[] {
  return input
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function uniq<T>(items: T[]): T[] {
  return [...new Set(items)];
}

/** Regroupe et compte les occurrences, utile pour les filtres à facettes. */
export function facetCounts<T>(items: T[], selector: (item: T) => string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    for (const key of selector(item)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return new Map([...counts].sort((a, b) => b[1] - a[1]));
}
